import * as React from "react";
import { useEffect } from "react";
import { Spinner } from "@graffer-inc/console-design-kit";
import { Redirect, useParams } from "react-router-dom";
import { connectPageService } from "../../../../application/domain/base/base-application-service";
import {
  UserDetailService,
  UserDetailServiceProps,
} from "../../../../application/service/smart-apply/user-detail/user-detail-service";
import { useModal } from "../../../../utils/custom-hooks";
import { SettingsTemplate } from "../../../template/settings/default-template";
import { PasswordResetConfirmModal } from "../../../organisms/smart-apply/modals/password-reset-confirm-modal";
import { LoginLockResetConfirmModal } from "../../../organisms/smart-apply/modals/login-lock-reset-confirm-modal";
import { SettingR } from "../route";
import { UserBasicInfo } from "../../../organisms/settings/user-detail";
import { useAuthContext } from "../../../../auth-state";
import { DisableTOTPConfirmModal } from "../../../organisms/smart-apply/modals/disable-totp-confirm-modal";
import { convertToFormattedId } from "@graffer-inc/cloud-gov-core";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";
import { Dropdown } from "../../../molecules/dropdown";
import { ChangeAuthMethodDialog } from "../../../organisms/settings/user-detail/change-auth-method-dialog";
import { UserAuthMethod } from "../../../../application/domain/user/user";

export const UserDetail: React.FC<UserDetailServiceProps> = ({
  user,
  groups,
  isLoading,
  setUserId,
  fetchUser,
  fetchGroupCandidates,
  updateUser,
  hasOrgSAMLSetting,
}) => {
  const authState = useAuthContext();

  const { id } = useParams<{ id: string }>();
  const dropdownModal = useModal(false);
  const passwordResetModal = useModal();
  const disableTOTPModal = useModal();
  const loginLockResetModal = useModal();
  const [isChangeAuthMethodDialogOpen, setIsChangeAuthMethodDialogOpen] =
    React.useState(false);

  const [showAuthMethodChangeMenu, setShowAuthMethodChangeMenu] =
    React.useState(false);

  const [changeTo, setChangeTo] = React.useState<UserAuthMethod | null>(null);

  useEffect(() => {
    setUserId(id);
    fetchUser();

    // 認証切り替えに関するメニューを表示するかの判定
    // SAML 認証が有効なテナントでなければ、そもそも表示する必要がない
    if (authState.data?.orgUsingFeature.useSamlAuth) {
      hasOrgSAMLSetting(authState.orgId).then((hasSetting) => {
        setShowAuthMethodChangeMenu(hasSetting);
      });
    }
  }, [id, setUserId, fetchUser]);

  useEffect(() => {
    if (!user) return;

    switch (user?.authMethod) {
      case "password":
        setChangeTo("saml");
        break;
      case "saml":
        setChangeTo("password");
        break;
      default:
        break;
    }
  }, [user]);

  if (!authState.hasPermissions("users.create")) {
    return <Redirect to={SettingR.MyAccount.link({})} />;
  }

  return (
    <SettingsTemplate
      breadcrumbs={[
        { to: SettingR.Users.link({}), title: "ユーザー一覧" },
        {
          to: SettingR.UserDetail.link({ id }),
          title: convertToFormattedId(id),
        },
      ]}
    >
      <Spinner loading={isLoading} />
      <div className="c-use-detail">
        <nav className="level">
          <div className="level-left">
            <h1 className="title">
              <i className="fa fa-user" /> ユーザー詳細
            </h1>
          </div>
          <div className="level-right">
            <div className="level-item">
              <OutsideClickHandler onOutsideClick={dropdownModal.close}>
                <Dropdown
                  title="ユーザー操作"
                  isActive={dropdownModal.isActive}
                  onClick={dropdownModal.open}
                  menus={[
                    {
                      title: "パスワードリセット",
                      onClick: passwordResetModal.open,
                      disable: !authState.hasPermissions(
                        "users.password_reset",
                      ),
                    },
                    {
                      title: "2段階認証の無効化",
                      onClick: disableTOTPModal.open,
                      disable: !authState.hasPermissions("users.disable_totp"),
                    },
                    {
                      title: "ロック解除",
                      onClick: loginLockResetModal.open,
                      disable: !authState.hasPermissions(
                        "users.login_lock_reset",
                      ),
                    },
                    showAuthMethodChangeMenu
                      ? {
                          title: "認証方法の変更",
                          onClick: () => setIsChangeAuthMethodDialogOpen(true),
                          disable: !authState.hasPermissions(
                            "users.auth_method_update",
                          ),
                        }
                      : false,
                  ]}
                />
              </OutsideClickHandler>
            </div>
          </div>
        </nav>
        <hr />
        {user && (
          <div className="c-use-detail--wrapper">
            <UserBasicInfo
              user={user}
              groups={groups}
              fetchUser={fetchUser}
              fetchGroupCandidates={fetchGroupCandidates}
              updateUser={updateUser}
              isServiceProvider={authState.isServiceProviderAccount()}
            />
          </div>
        )}
      </div>
      {user && (
        <>
          <PasswordResetConfirmModal
            title="パスワードリセット"
            user={user}
            isActive={passwordResetModal.isActive}
            onClose={() => {
              passwordResetModal.close();
              fetchUser();
            }}
          />
          <DisableTOTPConfirmModal
            title="2段階認証の無効化"
            user={user}
            isActive={disableTOTPModal.isActive}
            onClose={() => {
              disableTOTPModal.close();
              fetchUser();
            }}
          />
          <LoginLockResetConfirmModal
            title="ログインロックの解除"
            user={user}
            isActive={loginLockResetModal.isActive}
            onClose={() => {
              loginLockResetModal.close();
              fetchUser();
            }}
          />
          <ChangeAuthMethodDialog
            open={isChangeAuthMethodDialogOpen}
            user={user}
            onCloseDialog={() => setIsChangeAuthMethodDialogOpen(false)}
            changeTo={changeTo}
          />
        </>
      )}
    </SettingsTemplate>
  );
};

export const UserDetailContainer = connectPageService(
  UserDetail,
  () => new UserDetailService(),
);
